import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, FormControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { SearchableMultiSelectComponent } from '../../shared/searchable-multi-select/searchable-multi-select.component'; // IMPORT THIS
import { ShareItemsService } from '../../services/share-feature/share-items.service';
import { UserService } from '../../services/user/user.service';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { SharePopupData } from '../../interfaces/sharePopup';
import { forkJoin, of, Observable, Subject } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-share-popup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, ButtonsComponent, SearchableMultiSelectComponent],
  providers: [ShareItemsService],
  templateUrl: './share-popup.component.html',
  styleUrls: ['./share-popup.component.css'],
})
export class SharePopupComponent implements OnInit, OnDestroy {
  public Ids!: number[];
  public title!: string;
  public displayLimit = 3;
  private colors = ['#F972FE', '#A583FE', '#27C69A', '#5C6BC0', '#FFB74D'];
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';
  btnStyleFilledSmallForPrimaryBtnDisabled = 'btn-filled-small-disabled';
  btnMatFlat: string = 'mat-flat-button';
  shareForm!: FormGroup;
  teamControl = new FormControl([]);
  allGroups: string[] = [];
  public resource_path!: string;

  private groupNameToIdMap = new Map<string, number>();

  buttonCancel = $localize`Cancel`;
  buttonShare = $localize`Share`;
  btnMat = 'mat-button';
  btnStyleText = 'btn-text';

  isSubmitting = false;
  private destroy$ = new Subject<void>();
  constructor(
    public dialogRef: MatDialogRef<SharePopupComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SharePopupData,
    private fb: FormBuilder,
    private shareItemsService: ShareItemsService,
    private messageModalService: MessageModalService,
    private userService: UserService
  ) {
    if (data) {
      this.Ids = data.Ids;
      this.title = data.title;
      this.resource_path = data.resource_path;
    }
  }

  ngOnInit(): void {
    this.shareForm = this.fb.group({
      teams: this.teamControl,
    });

    this.loadUserGroups();
  }

  loadUserGroups(): void {
    const userIdInString = this.userService.user?.id ?? null;
    if (userIdInString) {
      const userId = parseInt(userIdInString, 10);
      if (!isNaN(userId)) {
        this.shareItemsService
          .getUserGroups(userId)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              if (response.success && Array.isArray(response.data)) {
                this.allGroups = response.data.map((group: any) => group.Name);
                response.data.forEach((group: any) => {
                  this.groupNameToIdMap.set(group.Name, group.GroupID);
                });
              }
            },
            error: (err) => {
              this.messageModalService.openModal($localize`Error`, `Failed to load user groups`);
            },
          });
      } else {
        this.messageModalService.openModal($localize`Error`, $localize`Invalid Id, Cannot Load Groups`);
      }
    } else {
      this.messageModalService.openModal($localize`Error`, $localize`Invalid Id, Cannot Load Groups`);
    }
  }

  getColor(index: number): string {
    return this.colors[index % this.colors.length];
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onShare(): void {
    this.isSubmitting = true;

    const selectedGroupNames: string[] = this.shareForm.value.teams || [];
    const selectedGroupIds = selectedGroupNames
      .map((name) => this.groupNameToIdMap.get(name))
      .filter((id): id is number => id !== undefined);

    this.shareItemsService
      .updateResourceGroupMemberships(this.resource_path, this.Ids, selectedGroupIds)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isSubmitting = false;
          if (response.success) {
            this.messageModalService.openModal('Success', `${this.title}(s) shared successfully.`);
            this.dialogRef.close(true);
          } else {
            this.messageModalService.openModal('Error', response.message || 'Failed to share the selected items.');
          }
        },
        error: (err) => {
          this.isSubmitting = false;
          this.messageModalService.openModal('Error', 'An unexpected error occurred during the share operation.');
          console.error('Share operation failed:', err);
        },
      });
  }

  getFormControl(controlName: string): FormControl {
    return this.shareForm.get(controlName) as FormControl;
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
